import * as React from "react"
import * as ReactDOM from "react-dom"
import {createStore, Store, applyMiddleware, Action, compose} from "redux"
import thunkMiddleware from "redux-thunk"

import {CallJs} from "../../lib/types";
import {Axis, DisplayModes, standardModes} from "../../lib/report-builder/graph-renderer";
import {makeReportApp, ReportAction, ReportState, ReportStoreOwnProps} from "../../lib/report-builder/lib";
import {FilterWidgetValueParams} from "../../lib/report-builder";
import {
	DateFilterKey, dateFromFilter, FilterState,
	ReportTypeData
} from "../../lib/report-builder/lib2";
import {reducer as reportManagerReducer} from "../../lib/report-builder"
import {dateToYYYYMMDD, DispatcherActionLike, flatten, withDefault} from "../../lib/utils";
import {CommandCaller} from "../../lib/commands";
import {FlatReportChartData, flattenReportChartData, ReportChartData} from "../../lib/report-builder/chart-data";

type CallJsParams = {
	ajaxPageUrl: string,
	types: TypeDataCallJs[]
}

type TypeDataCallJs = {
	serial: string|number,
	title: string,
}

type SearchDirectoryReportAxes = {
	searches: Axis,
	searchResults: Axis,
	profileViews: Axis,
	profileEnquiries: Axis,
	profileStats: Axis
}

type SearchDirectoryReportFilterTypes = "dateRange"|"types"
type SearchDirectoryReportFilters = {[k in SearchDirectoryReportFilterTypes]: FilterWidgetValueParams}

type SearchDirectoryReportTypes = "searchCount"|"searchResultsCount"|"numProfileView"|"numProfileEnquiry"|"overallProfileStats"
type SearchDirectoryReportData = {[k in SearchDirectoryReportTypes]: ReportTypeData<SearchDirectoryReportAxes, SearchDirectoryReportFilters, DisplayModes>}

type SearchDirectoryReportState = ReportState<SearchDirectoryReportAxes, SearchDirectoryReportFilters, DisplayModes, SearchDirectoryReportData>
type SearchDirectoryReportOwnProps = ReportStoreOwnProps<SearchDirectoryReportAxes, SearchDirectoryReportFilters, DisplayModes, SearchDirectoryReportData, SearchDirectoryReportState>

type ReportRunDateParams = {
	startDate: string,
	endDate: string
}

type ReportRunDateAndTypeParams = ReportRunDateParams & {typeSerials: undefined|string[]}

const yyyymmddFromDateFilter: (filter: FilterState, key: DateFilterKey) => string = compose(dateToYYYYMMDD, dateFromFilter)
const reportRunDates = (filter: FilterState): ReportRunDateParams => ({
	startDate: yyyymmddFromDateFilter(filter, 'fromDate'),
	endDate: yyyymmddFromDateFilter(filter, 'toDate'),
})

const runSearchReport = async (command: "get search count"|"get search results count", dateRangeFilter: FilterState, typeFilter: FilterState, ajaxPageUrl: string) => {
	const typeSerials = typeFilter.type == "checkbox" ? typeFilter.checked : []
	const params: ReportRunDateAndTypeParams = {
		...reportRunDates(dateRangeFilter),
		typeSerials: typeSerials && typeSerials.length ? typeSerials : undefined
	}
	return await new CommandCaller(ajaxPageUrl).runGet(command, params) as ReportChartData
}

function runProfileReport(command: "get profile view count"|"get profile enquiry count", dateRangeFilter: FilterState, ajaxPageUrl: string): Promise<ReportChartData>
function runProfileReport(command: "get profile stats count", dateRangeFilter: FilterState, ajaxPageUrl: string): Promise<ReportRunOverallStatsResult[]>
async function runProfileReport(command: string, dateRangeFilter: FilterState, ajaxPageUrl: string) {
	const params: ReportRunDateParams = reportRunDates(dateRangeFilter)
	return await new CommandCaller(ajaxPageUrl).runGet(command, params) as ReportChartData & ReportRunOverallStatsResult[]/*this type will depend on the type of the `command`. it's actually an either type, but this helps the overload*/
}

type ReportRunOverallStatsResult = {
	EntityName: string,
	EntitySerial: string,
	StatsSearch: number
	StatsView: number,
	StatsEnquiry: number
}

type Command = "get search count" | "get search results count" | "get profile view count" | "get profile enquiry count" | "get profile stats count"
type SearchDirectoryReportAction = ReportAction<SearchDirectoryReportAxes, SearchDirectoryReportFilters, DisplayModes, SearchDirectoryReportData>
export const runReports: CallJs<CallJsParams> = (callback, params) => {
	const div = document.createElement("div");

	const content = document.getElementById('content')
	if (content) {
		content.getElementsByTagName('table')[0].style.width = '100%';
	}

	const types = params.types.map(t => ({value: "" + t.serial, label: t.title}))
	const axes: SearchDirectoryReportAxes = {
		searches: [{value: "0", label: "Total Search Count"}],
		searchResults: [{value: "0", label: "Total Search Result Count"}, ...types],
		profileViews: [{value: "0", label: "Total Profile Views"}],
		profileEnquiries: [{value: "0", label: "Total Profile Enquiries"}],
		profileStats: [
			{value: "StatsSearch", label: "Search Total"},
			{value: "StatsView", label: "View Total"},
			{value: "StatsEnquiry", label: "Enquiry Total"},
		]
	}

	const reducer: (s: SearchDirectoryReportState, a: Action) => SearchDirectoryReportState = reportManagerReducer

	const filters: SearchDirectoryReportFilters = {
		dateRange: {type: "date range", isHidden: false},
		types: {
			type: "checkbox",
			isHidden: false,
			label: "Membership Types",
			group: types
		},
	}

	const valueKeys = [{id: "Date", label: "Date"}]
	const displayModes = ["Graph", "Table"]

	const reportTypes: SearchDirectoryReportData = {
		searchCount: {
			axis: "searches",
			filters: ["dateRange", "types"],
			label: "Search Count",
			renderAs: "line",
			removeEmptyAxes: true,
			keys: valueKeys,
			defaultDisplayMode: "Graph",
			displayModes: displayModes
		},
		searchResultsCount: {
			axis: "searchResults",
			filters: ["dateRange", "types"],
			label: "Search Results Count",
			renderAs: "line",
			removeEmptyAxes: true,
			keys: valueKeys,
			defaultDisplayMode: "Graph",
			displayModes: displayModes
		},
		numProfileView: {
			axis: "profileViews",
			filters: ["dateRange"],
			label: "Number of Profile View",
			renderAs: "line",
			removeEmptyAxes: true,
			keys: valueKeys,
			defaultDisplayMode: "Graph",
			displayModes: displayModes
		},
		numProfileEnquiry: {
			axis: "profileEnquiries",
			filters: ["dateRange"],
			label: "Number of Profile Enquiry",
			renderAs: "line",
			removeEmptyAxes: true,
			keys: valueKeys,
			defaultDisplayMode: "Graph",
			displayModes: displayModes
		},
		overallProfileStats: {
			axis: "profileStats",
			filters: ["dateRange"],
			label: "Overall Profile Stats",
			renderAs: "bar",
			removeEmptyAxes: false,
			keys: [{id: "EntityName", label: "Entity Name"}],
			defaultDisplayMode: "Table",
			displayModes: ["Table"]
		},
	}

	const mapReportTypeToCommand = (reportType: SearchDirectoryReportTypes): Command => {
		switch (reportType) {
			case "searchCount":
				return "get search count"
			case "searchResultsCount":
				return "get search results count"
			case "numProfileView":
				return "get profile view count"
			case "numProfileEnquiry":
				return "get profile enquiry count"
			case "overallProfileStats":
				return "get profile stats count"
		}
	}

	const runSomeReport = async (ajaxPageUrl: string, state: SearchDirectoryReportState, dispatchWrapped: DispatcherActionLike<SearchDirectoryReportAction, Action>) => {
		const reportType = state.reportType
		const command = mapReportTypeToCommand(reportType)
		const axis = reportTypes[reportType].axis

		switch (command) {
			case "get search count":
			case "get search results count":
				const a = await runSearchReport(
					command,
					state.filters.dateRange,
					state.filters.types,
					ajaxPageUrl
				)
				dispatchWrapped({
					type: "set new report data",
					data: {data: flattenReportChartData(a), renderAs: "line", axis}
				})
				break
			case "get profile view count":
			case "get profile enquiry count":
				const c = await runProfileReport(
					command,
					state.filters.dateRange,
					ajaxPageUrl
				)
				dispatchWrapped({
					type: "set new report data",
					data: {data: flattenReportChartData(c), renderAs: "line", axis}
				})
				break;
			case "get profile stats count":
				const e = await runProfileReport(
					"get profile stats count",
					state.filters.dateRange,
					ajaxPageUrl
				)
				dispatchWrapped({
					type: "set new report data",
					data: {
						renderAs: "bar",
						axis,
						data: flatten(e.map((item): FlatReportChartData => [
							{id: 'StatsSearch', key: item.EntityName, value: item.StatsSearch},
							{id: 'StatsView', key: item.EntityName, value: item.StatsView},
							{id: 'StatsEnquiry', key: item.EntityName, value: item.StatsEnquiry},
						]))
					}
				})
				break;
		}
	}

	const ownProps = (store: Store<SearchDirectoryReportState>, ajaxPageUrl:string): SearchDirectoryReportOwnProps => ({
		title: "Search Directory Reports",
		store: store,
		modes: standardModes,
		reportTypes: reportTypes,
		filters: filters,
		axes: axes,
		runReport: (dispatchWrapped) => async (dispatchedUnwrapped, getState) => {
			const state = getState()
			try {
				return runSomeReport(ajaxPageUrl, state, dispatchWrapped)
			} catch (e) {
				window.alert("There was an unexpected error. Ensure you're logged in, your internet connection is working, and try reloading the page")
			}
		}
	})

	const initialState: SearchDirectoryReportState = {
		reportType: "searchCount",
		displayMode: "Graph",
		filters: {
			"dateRange": {type: "date range", fromDate: new Date(), toDate: new Date()},
			"types": {type: "checkbox", checked: []}
		}
	}

	callback(div)
	const store = createStore(withDefault(reducer, initialState), initialState, applyMiddleware(thunkMiddleware))
	const ReportApp = makeReportApp<SearchDirectoryReportAxes, SearchDirectoryReportFilters, DisplayModes, SearchDirectoryReportData, SearchDirectoryReportState>()

	ReactDOM.render(<ReportApp {...ownProps(store, params.ajaxPageUrl)} />, div)

	// 😛
}



// WEBPACK FOOTER //
// ./src/searchdirectory/searchdirectory-reports/app.tsx